import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Drawer,
  Chip,
  useMediaQuery,
  FormControlLabel,
  Switch,
  Pagination,
  PaginationItem,
  Rating,
  alpha,
  Container,
  InputAdornment,
  IconButton,
  Autocomplete,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import PrimaryButton from "../../components/button/button";
import CenteredBox from "../../components/centeredBox/centeredBox";
import { useSelector, useDispatch } from "react-redux";
import { LocationOn, Search, FilterList, Close, DateRange, AccessTime } from "@mui/icons-material";
import { searchTutors } from "../../state/slices/search";
import { useNavigate } from "react-router-dom";
import Loading from "../../components/loading/loading";
import { extractId } from "../../utils/imageIdExtractor";
import ethiopiaUniversities from "../../utils/ethiopiaUniversities.json";

function App() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const SearchState = useSelector((state) => state.search);
  console.log(SearchState.tutors);
  const loading = SearchState.loading;

  const handleFilterOpen = () => {
    setIsFilterOpen(true);
  };

  const handleFilterClose = () => {
    setIsFilterOpen(false);
  };

  const dispatch = useDispatch();
  console.log(SearchState);
  const [formData, setFormData] = useState({
    subjects: SearchState.filter ? SearchState.filter.subjects : [],
    minPrice: "",
    maxPrice: "",
    universities: [],
    locations: [],
    gradeLevels: SearchState.filter ? SearchState.filter.gradeLevels : [],
    tutoringOptions: SearchState.filter
      ? SearchState.filter.tutoringOptions
      : [],
    date: SearchState.filter ? SearchState.filter.date : '',
    startTime: SearchState.filter ? SearchState.filter.startTime : '',
    endTime: SearchState.filter ? SearchState.filter.endTime : '',
    duration: SearchState.filter ? SearchState.filter.duration : '',
    pageSize: 10,
    pageNumber: 1,
  });

  const handleTutoringTypeChange = (event) => {
    const { value } = event.target;
    const selectedTypes = [...formData.tutoringOptions];

    if (event.target.checked) {
      selectedTypes.push(value);
    } else {
      const index = selectedTypes.indexOf(value);
      if (index !== -1) {
        selectedTypes.splice(index, 1);
      }
    }
    setFormData({
      ...formData,
      tutoringOptions: selectedTypes,
    });
  };

  const handleFilterSubmit = () => {
    // Exclude duration from API call - it's only used for request form pre-population
    const { duration, ...searchParams } = formData;
    dispatch(searchTutors(searchParams));
    handleFilterClose();
  };

  const handlePageChange = (event, page) => {
    setFormData({
      ...formData,
      pageNumber: page,
    });
    // Exclude duration from API call - it's only used for request form pre-population
    const { duration, ...searchParams } = formData;
    dispatch(searchTutors({ ...searchParams, pageNumber: page }));
  };

  return (
    <>
      {
        loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
          >
            <Loading />
          </Box>
        ) : (
          <Box sx={{ minHeight: '100vh', background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`, py: 4 }}>
            <Container maxWidth="xl">
              {/* Header Section */}
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1 }}>
                  {SearchState.totalCount} {t('result.results')}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  {t('result.findYourPerfectTutor')}
                </Typography>
              </Box>

              <Grid container spacing={4}>
                {/* Filter Sidebar */}
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: { xs: "none", md: "block" }, position: 'sticky', top: 100 }}>
                    <Filter
                      handleFilterSubmit={handleFilterSubmit}
                      handleTutoringTypeChange={handleTutoringTypeChange}
                      formData={formData}
                      setFormData={setFormData}
                    />
                  </Box>
                  <Box
                    sx={{ display: { xs: "flex", md: "none" }, justifyContent: "flex-end", mb: 2 }}
                  >
                    <Button
                      onClick={handleFilterOpen}
                      variant="contained"
                      startIcon={<FilterList />}
                      sx={{
                        background: theme.palette.primary.main,
                        color: theme.palette.secondary.main,
                        borderRadius: 3,
                        px: 3,
                        py: 1,
                        fontWeight: 600,
                        "&:hover": {
                          background: theme.palette.primary.dark,
                        },
                      }}
                    >
                      {t('result.filter')}
                    </Button>
                    <Drawer
                      anchor="right"
                      open={isFilterOpen}
                      onClose={handleFilterClose}
                      PaperProps={{
                        sx: {
                          width: { xs: '85%', sm: '400px' },
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                        }
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        p: 3,
                        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        flexShrink: 0,
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                          {t('result.filter')}
                        </Typography>
                        <IconButton onClick={handleFilterClose}>
                          <Close />
                        </IconButton>
                      </Box>
                      <Box sx={{ 
                        flex: 1, 
                        overflowY: 'auto', 
                        p: 3,
                        pb: 4,
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: alpha(theme.palette.primary.main, 0.05),
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: alpha(theme.palette.primary.main, 0.3),
                          borderRadius: '4px',
                          '&:hover': {
                            background: alpha(theme.palette.primary.main, 0.5),
                          },
                        },
                      }}>
                        <Filter
                          handleFilterSubmit={handleFilterSubmit}
                          handleTutoringTypeChange={handleTutoringTypeChange}
                          formData={formData}
                          setFormData={setFormData}
                          isInDrawer={true}
                        />
                      </Box>
                    </Drawer>
                  </Box>
                </Grid>

                {/* Results Grid */}
                <Grid item xs={12} md={9}>
                  {SearchState.tutors.length === 0 ? (
                    <Card
                      elevation={0}
                      sx={{
                        p: 6,
                        textAlign: 'center',
                        borderRadius: 4,
                        bgcolor: 'white',
                      }}
                    >
                      <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('result.noApprovedTutors')}
                      </Typography>
                    </Card>
                  ) : (
                    <Grid container spacing={3}>
                      {SearchState.tutors.map((person, index) => (
                        <Grid item xs={12} key={index}>
                          <Card
                            elevation={0}
                            onClick={() => navigate("/detail-result", { 
                              state: { 
                                person,
                                searchFilters: {
                                  date: formData.date || SearchState.filter?.date,
                                  startTime: formData.startTime || SearchState.filter?.startTime,
                                  endTime: formData.endTime || SearchState.filter?.endTime,
                                  duration: formData.duration || SearchState.filter?.duration,
                                  tutoringOptions: formData.tutoringOptions || SearchState.filter?.tutoringOptions || []
                                }
                              } 
                            })}
                            sx={{
                              width: "100%",
                              borderRadius: 4,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              bgcolor: 'white',
                              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                              '&:hover': {
                                transform: 'translateY(-8px)',
                                boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
                              }
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Grid container spacing={3} alignItems="center">
                                <Grid item xs={12} sm="auto">
                                  <Avatar
                                    src={person.profileImage?.includes('drive.google.com') ? `https://drive.lienuc.com/uc?id=${extractId(person.profileImage)}` : person.profileImage}
                                    alt={`${person.firstName} ${person.lastName}`}
                                    sx={{
                                      width: 100,
                                      height: 100,
                                      border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm>
                                  <Stack spacing={1.5}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                                      <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                                        {person.firstName} {person.lastName}
                                      </Typography>
                                      <Box sx={{ 
                                        px: 2, 
                                        py: 0.5, 
                                        borderRadius: 3, 
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                      }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                                          {person.rate} {t('result.etbPerHour')}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                      }}
                                    >
                                      {person.experience}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                      {(() => {
                                        // Handle both old format (array of strings) and new format (array of objects)
                                        const subjectsList = person.subjects && person.subjects.length > 0
                                          ? (typeof person.subjects[0] === 'string'
                                              ? person.subjects.map(s => ({ subject: s, gradeLevel: '' }))
                                              : person.subjects.map(s => ({
                                                  subject: s.subject || s,
                                                  gradeLevel: s.gradeLevel || ''
                                                })).filter(s => s.subject))
                                          : [];
                                        
                                        return (
                                          <>
                                            {subjectsList.slice(0, 3).map((subjectObj, idx) => {
                                              const label = subjectObj.gradeLevel 
                                                ? `${subjectObj.subject} (${subjectObj.gradeLevel})`
                                                : subjectObj.subject;
                                              return (
                                                <Chip
                                                  key={idx}
                                                  label={label}
                                                  size="small"
                                                  sx={{
                                                    bgcolor: alpha(theme.palette.secondary.main, 0.2),
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 600,
                                                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
                                                  }}
                                                />
                                              );
                                            })}
                                            {subjectsList.length > 3 && (
                                              <Chip
                                                label={`+${subjectsList.length - 3}`}
                                                size="small"
                                                sx={{
                                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                  color: theme.palette.primary.main,
                                                  fontWeight: 600,
                                                }}
                                              />
                                            )}
                                          </>
                                        );
                                      })()}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <LocationOn sx={{ fontSize: 18, color: theme.palette.primary.main, opacity: 0.7 }} />
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                          {person.location}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Rating name="read-only" value={person.ratings} readOnly size="small" />
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                          ({person.totalReviews} {t('result.reviews')})
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Stack>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {SearchState.tutors.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, mb: 4 }}>
                  <Pagination
                    count={Math.ceil(SearchState.totalCount / formData.pageSize)}
                    page={formData.pageNumber}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontSize: '1rem',
                        fontWeight: 600,
                        '&.Mui-selected': {
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                          '&:hover': {
                            bgcolor: theme.palette.primary.dark,
                          }
                        }
                      },
                    }}
                    boundaryCount={1}
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                  />
                </Box>
              )}
            </Container>
          </Box>)
      }
    </>
  );
}

const Filter = ({
  setFormData,
  formData,
  handleTutoringTypeChange,
  handleFilterSubmit,
  isInDrawer = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const FilterContent = (
    <>
      {!isInDrawer && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <FilterList />
          {t('result.filter')}
        </Typography>
      )}
      
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}>
            {t('result.subject')}
          </Typography>
          <TextField
            label={t('result.subject')}
            variant="outlined"
            onChange={(event) =>
              setFormData({ ...formData, subjects: [event.target.value] })
            }
            fullWidth
            size="small"
            value={formData.subjects}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: theme.palette.primary.main, opacity: 0.7 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}>
            {t('result.price')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label={t('result.min')}
                variant="outlined"
                size="small"
                type="number"
                onChange={(event) =>
                  setFormData({ ...formData, minPrice: event.target.value })
                }
                value={formData.minPrice}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t('result.max')}
                variant="outlined"
                size="small"
                type="number"
                onChange={(event) =>
                  setFormData({ ...formData, maxPrice: event.target.value })
                }
                value={formData.maxPrice}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DateRange sx={{ fontSize: 18 }} />
            {t('home.when')}
          </Typography>
          <TextField
            label={t('home.when')}
            variant="outlined"
            size="small"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            onChange={(event) =>
              setFormData({ ...formData, date: event.target.value, startTime: '', endTime: '' })
            }
            value={formData.date}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
          {/* Time Window - Show when date is selected */}
          {formData.date && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={t('home.startTime') || 'Start Time'}
                    variant="outlined"
                    size="small"
                    type="time"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTime sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                    onChange={(event) =>
                      setFormData({ ...formData, startTime: event.target.value })
                    }
                    value={formData.startTime}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={t('home.endTime') || 'End Time'}
                    variant="outlined"
                    size="small"
                    type="time"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTime sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                    onChange={(event) =>
                      setFormData({ ...formData, endTime: event.target.value })
                    }
                    value={formData.endTime}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOn sx={{ fontSize: 18 }} />
            {t('result.location')}
          </Typography>
          <TextField
            label={t('result.location')}
            variant="outlined"
            size="small"
            fullWidth
            onChange={(event) =>
              setFormData({ ...formData, locations: [event.target.value] })
            }
            value={formData.locations}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}>
            {t('tutorProfile.gradeLevel')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6',
              'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12',
              'College'].map((grade) => {
              const isSelected = formData.gradeLevels?.includes(grade);
              return (
                <Chip
                  key={grade}
                  label={grade}
                  onClick={() => {
                    const gradeLevels = formData.gradeLevels || [];
                    const newGradeLevels = isSelected
                      ? gradeLevels.filter((g) => g !== grade)
                      : [...gradeLevels, grade];
                    setFormData({ ...formData, gradeLevels: newGradeLevels });
                  }}
                  sx={{
                    bgcolor: isSelected
                      ? theme.palette.primary.main
                      : alpha(theme.palette.primary.main, 0.1),
                    color: isSelected ? 'white' : theme.palette.primary.main,
                    border: `2px solid ${
                      isSelected
                        ? theme.palette.primary.main
                        : alpha(theme.palette.primary.main, 0.3)
                    }`,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    height: '28px',
                    '&:hover': {
                      bgcolor: isSelected
                        ? theme.palette.primary.dark
                        : alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}>
            {t('result.tutoringType')}
          </Typography>
          <Stack spacing={1}>
            <Card
              sx={{
                p: 1.5,
                border: `2px solid ${formData.tutoringOptions.includes("REMOTE") ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 2,
                bgcolor: formData.tutoringOptions.includes("REMOTE") ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                transition: 'all 0.3s ease',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.tutoringOptions.includes("REMOTE")}
                    onChange={handleTutoringTypeChange}
                    value="REMOTE"
                    color="primary"
                  />
                }
                label={t('common.online')}
              />
            </Card>
            <Card
              sx={{
                p: 1.5,
                border: `2px solid ${formData.tutoringOptions.includes("IN_PERSON") ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 2,
                bgcolor: formData.tutoringOptions.includes("IN_PERSON") ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                transition: 'all 0.3s ease',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.tutoringOptions.includes("IN_PERSON")}
                    onChange={handleTutoringTypeChange}
                    value="IN_PERSON"
                    color="primary"
                  />
                }
                label={t('common.inPerson')}
              />
            </Card>
          </Stack>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}>
            {t('result.university')}
          </Typography>
          <Autocomplete
            freeSolo
            options={ethiopiaUniversities.universities}
            value={formData.universities && formData.universities.length > 0 ? formData.universities[0] : ''}
            onInputChange={(event, newInputValue) => {
              setFormData({ ...formData, universities: newInputValue ? [newInputValue] : [] })
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('result.university')}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            )}
          />
        </Box>

        <Button
          onClick={handleFilterSubmit}
          fullWidth
          variant="contained"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: theme.palette.secondary.main,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 700,
            textTransform: 'capitalize',
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.5)}`,
            }
          }}
        >
          {t('result.applyFilter')}
        </Button>
      </Stack>
    </>
  );

  if (isInDrawer) {
    return FilterContent;
  }

  return (
    <Card 
      elevation={0}
      sx={{ 
        p: 3, 
        borderRadius: 4,
        bgcolor: 'white',
        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
        position: 'sticky',
        top: 100,
      }}
    >
      {FilterContent}
    </Card>
  );
};

export default App;
